import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AuthLayoutProps {
  children: React.ReactNode;
  planTitle: string;
  planTagline: string;
  planFeatures: { text: string }[];
  planPriceLabel?: string;
  planPriceSubtext?: string;
  /** Two-to-three sentence pitch shown below the headline. */
  description?: string;
  /** Bottom-of-pane brand line. Falls back to the company tagline. */
  bottomLine?: string;
  /**
   * Opt-in mobile plan reminder under the form. Only signup uses it —
   * login / forgot-password / reset don't have a plan to recap. Other
   * pages get a clean form-only mobile layout (no upper bar either).
   */
  showMobilePlanSummary?: boolean;
}

const HERO_GRADIENT = `
  radial-gradient(circle at top calc(50% + 10px) right calc(0% - 120px), var(--color-gradient-pink) 0%, transparent 25%),
  radial-gradient(circle at bottom calc(0% - 100px) right calc(0% - 150px), var(--color-gradient-purple) 0%, transparent 25%),
  linear-gradient(105deg, var(--color-light) 0%, var(--color-light-lin) 60%, var(--color-gradient-light) 100%)
`;

const DOT_PATTERN = `radial-gradient(circle at 2px 2px, rgba(23, 29, 39, 0.15) 2px, transparent 0)`;

/**
 * Two-pane auth shell.
 *
 * Desktop: 50/50 split — left pane mirrors the landing-page hero (same
 * gradient + dot pattern) but without the product Lottie, so the eye
 * lands on the form instead of competing with an animated character.
 *
 * Mobile: the form fills the screen — no top bar, no gradient, nothing
 * stacked above. Signup opts into a soft plan reminder under the form
 * via `showMobilePlanSummary` (it's the only auth flow with a plan to
 * recap). Everything else just shows the form on white.
 */
const MOBILE_FEATURE_LIMIT = 2;

export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  planTitle,
  planTagline,
  planFeatures,
  planPriceLabel,
  planPriceSubtext,
  description,
  bottomLine,
  showMobilePlanSummary = false,
}) => {
  const { t } = useTranslation('auth');
  const brandTagline = bottomLine ?? t('signup.brand_tagline');
  const mobileFeatures = planFeatures.slice(0, MOBILE_FEATURE_LIMIT);

  return (
    <div className="min-h-[100dvh] w-full bg-white flex flex-col lg:flex-row">
      {/* Desktop left pane — hero gradient + dot pattern only */}
      <aside
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ background: HERO_GRADIENT }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: DOT_PATTERN, backgroundSize: '50px 50px' }}
          aria-hidden="true"
        />

        <div className="relative z-10 flex flex-col justify-between gap-10 p-12 xl:p-16 w-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img
                src="/logo/dark-logo.png"
                alt="Invirtus"
                className="w-10 h-10 xl:w-11 xl:h-11"
              />
              <span className="text-2xl xl:text-3xl font-heading font-bold tracking-tight text-dark-invirtus">
                Invirtus
              </span>
            </div>
            <div className="text-xs font-semibold text-purple uppercase tracking-[0.2em]">
              {planTagline}
            </div>
          </div>

          <div className="max-w-xl flex-1 flex flex-col justify-center gap-8">
            <div className="space-y-5">
              <h2 className="text-4xl xl:text-5xl font-heading font-bold leading-[1.1] tracking-tight text-dark-invirtus">
                {planTitle}
              </h2>
              {description && (
                <p className="text-lg text-dark-invirtus/70 leading-relaxed font-medium">
                  {description}
                </p>
              )}
              {planPriceLabel && (
                <div className="flex items-baseline gap-2 pt-1">
                  <span className="text-4xl font-bold text-dark-invirtus">
                    {planPriceLabel}
                  </span>
                  {planPriceSubtext && (
                    <span className="text-sm text-dark-invirtus/60">
                      {planPriceSubtext}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="h-px w-16 bg-dark-invirtus/20" aria-hidden="true" />

            <ul className="space-y-3.5">
              {planFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-white/70 border border-dark-invirtus/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-dark-invirtus" />
                  </span>
                  <span className="text-base text-dark-invirtus/85 leading-relaxed">
                    {f.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-3 pt-8 border-t border-dark-invirtus/10">
            <p className="text-base font-heading font-semibold text-dark-invirtus leading-snug max-w-md">
              {brandTagline}
            </p>
            <p className="text-xs text-dark-invirtus/50">
              {t('signup.brand_copyright', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </aside>

      {/* Right pane — content */}
      <main className="flex-1 flex items-center justify-center px-6 py-8 lg:p-12 bg-white">
        <div className="w-full max-w-md">{children}</div>
      </main>

      {/* Mobile-only plan recap — signup opt-in. Single-line meta
          strip: tag → bullet-separated features → price. No card, no
          icons, no checklist — just a quiet line of context that sits
          under the legal copy. */}
      {showMobilePlanSummary && (
        <div className="lg:hidden bg-white px-6 pb-8 pt-1">
          <div className="mx-auto max-w-md flex items-center gap-2 text-[12px] leading-snug">
            <span className="font-semibold uppercase tracking-[0.14em] text-purple text-[10.5px] flex-shrink-0">
              {planTagline}
            </span>
            <span className="text-dark-invirtus/30" aria-hidden="true">|</span>
            <span className="text-dark-invirtus/65 truncate">
              {mobileFeatures.map((f) => f.text).join(' · ')}
            </span>
            {planPriceLabel && (
              <span className="ml-auto font-semibold text-dark-invirtus flex-shrink-0">
                {planPriceLabel}
                {planPriceSubtext && (
                  <span className="ml-1 text-[10px] font-normal text-dark-invirtus/50">
                    {planPriceSubtext}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
